import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  LayoutDashboard,
  Settings,
  ChevronDown,
  LogOut,
  ChefHat,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useAuthStore } from '@/features/auth/store';
import { useRestaurantStore } from '@/features/restaurants/store';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';
import { NotificationBell } from '@/features/notifications';
import { DeauthorizedBanner } from '@/features/payments';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { restaurantId } = useParams();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const restaurants = useRestaurantStore((s) => s.restaurants);
  const currentRestaurant = useRestaurantStore((s) => s.currentRestaurant);
  const setCurrentRestaurant = useRestaurantStore((s) => s.setCurrentRestaurant);

  // Check if we're in a restaurant context - RestaurantLayout handles its own sidebar
  const isRestaurantContext = !!restaurantId;

  const handleRestaurantSwitch = (restaurantId: string) => {
    const restaurant = restaurants.find(r => r.id === restaurantId);
    if (restaurant) {
      setCurrentRestaurant(restaurant);
      navigate(`/app/restaurants/${restaurantId}`);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-[100dvh] min-h-screen bg-background">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-sm">
        <div className="flex h-16 items-center justify-between px-3 sm:px-4 lg:px-6 gap-2">
          {/* Left: Logo */}
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-shrink">
            <Link to="/app" className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg gradient-warm flex items-center justify-center shrink-0">
                <ChefHat className="w-4 h-4 sm:w-5 sm:h-5 text-accent-foreground" />
              </div>
              <span className="font-semibold hidden sm:block truncate">Restroos</span>
            </Link>
          </div>

          {/* Center: Restaurant Switcher */}
          {restaurantId && currentRestaurant && (
            <div className="flex-1 flex justify-center min-w-0 px-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-1.5 sm:gap-2 font-medium h-9 sm:h-10 px-2 sm:px-3 max-w-full">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md gradient-warm flex items-center justify-center text-xs font-bold text-accent-foreground shrink-0">
                      {currentRestaurant.name.charAt(0)}
                    </div>
                    <span className="hidden sm:block truncate text-xs sm:text-sm">{currentRestaurant.name}</span>
                    <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-56">
                  {restaurants.map((restaurant) => (
                    <DropdownMenuItem
                      key={restaurant.id}
                      onClick={() => handleRestaurantSwitch(restaurant.id)}
                      className={cn(restaurant.id === restaurantId && 'bg-secondary')}
                    >
                      <div className="w-6 h-6 rounded-md gradient-warm flex items-center justify-center text-xs font-bold text-accent-foreground mr-2">
                        {restaurant.name.charAt(0)}
                      </div>
                      <span className="truncate flex-1">{restaurant.name}</span>
                      {!restaurant.isActive && (
                        <span className="ml-auto text-xs text-muted-foreground shrink-0">{t('app.inactive')}</span>
                      )}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/app')}>
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {t('app.allRestaurants')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Right: Language + User Menu */}
          <div className="flex-shrink-0 flex items-center gap-1">
            {restaurantId && <NotificationBell restaurantId={restaurantId} />}
            <LanguageSwitcher />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-1.5 sm:gap-2 h-9 sm:h-10 px-2 sm:px-3">
                  <Avatar className="w-7 h-7 sm:w-8 sm:h-8">
                    <AvatarFallback className="bg-secondary text-xs sm:text-sm">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:block text-xs sm:text-sm font-medium truncate max-w-[120px]">{user?.name}</span>
                  <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/app/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  {t('app.accountSettings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('app.signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <DeauthorizedBanner />

      <div className="flex">
        {/* Main Content - No sidebar when in restaurant context (RestaurantLayout handles it) */}
        <main className={cn(
          "flex-1",
          !isRestaurantContext && "p-4 lg:p-8"
        )}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
